import { useContext, useEffect, useState } from "react";
import { callFunction } from "../api/callFunction";
import EventSuggestionAPI from "../api/EventSuggestionApi";
import VendorAPI from "../api/VendorApi";
import { db } from "../firebase";
import { fetchFunction } from "../api/fetchFunction";
import { readSSEStream } from "../utils/readSSEStream";
import { toast } from "react-toastify";
import { GlobalContext } from "../context/GlobalContext";
import {
  SparklesIcon,
  ChevronDownIcon,
  HandRaisedIcon,
  FunnelIcon,
  MapIcon,
  Squares2X2Icon,
  XMarkIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { MapView } from "./EventSuggestionsMap";
import { SuggestionCard } from "./SuggestionCard";
import { fetchUsersByDocIds } from "./utils";

export default function EventSuggestions() {
  const [generating, setGenerating] = useState(false);
  const [pipelineSteps, setPipelineSteps] = useState([]);
  const [view, setView] = useState("cards");
  const [customNotes, setCustomNotes] = useState("");
  const [showDirectives, setShowDirectives] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortField, setSortField] = useState("suggestedDate");
  const [pickupFilter, setPickupFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showProjectionInfo, setShowProjectionInfo] = useState(false);
  const [regions, setRegions] = useState([]);
  const [genRegionId, setGenRegionId] = useState("sf-bay");
  const [savingVideos, setSavingVideos] = useState(false);
  const [videosEnabled, setVideosEnabled] = useState(true);
  const { globalState } = useContext(GlobalContext);
  const { isSuperAdmin, vendorID, loading: authLoading } = globalState;

  // The suggestionVideosEnabled flag (clientConfig/features) gates GENERATION
  // only: off means new suggestions don't get a Veo clip made for them. Playback
  // of clips that already exist is never gated, so nothing but this toggle reads
  // the flag client-side. Default-on: only an explicit false disables.
  useEffect(() => {
    EventSuggestionAPI.isVideoGenerationEnabled()
      .then((enabled) => {
        setVideosEnabled(enabled);
      })
      .catch((err) => {
        console.error("[EventSuggestions] features flag read failed:", err);
      });
  }, []);

  const handleToggleVideos = async () => {
    if (savingVideos) return;
    const next = !videosEnabled;
    setVideosEnabled(next);
    setSavingVideos(true);
    try {
      await callFunction("adminUpdatePlatformFeatures", { suggestionVideosEnabled: next });
      toast.success(next ? "Video generation turned on" : "Video generation turned off");
    } catch (err) {
      console.error("[EventSuggestions] adminUpdatePlatformFeatures error:", err);
      setVideosEnabled(!next);
      toast.error("Failed to update video generation");
    } finally {
      setSavingVideos(false);
    }
  };

  // Region context. SuperAdmins get the full launchable list (to target
  // generation at any metro). A host gets their home region + the active regions
  // so we can softly default their suggestions view to their own city — they can
  // still switch to "All" or another region. Gated on authLoading so we don't run
  // the host branch for a superAdmin before their claim resolves.
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        if (isSuperAdmin) {
          const res = await callFunction("adminListRegions", {});
          const all = res?.regions || [];
          if (!cancelled) setRegions(all.filter((r) => r.status === "live" || r.status === "beta"));
        } else {
          const res = await callFunction("getHostRegionContext", {});
          if (cancelled) return;
          setRegions(res?.regions || []);
          const myRegionId = res?.myRegionId;
          if (myRegionId) {
            setRegionFilter(myRegionId);
            setGenRegionId(myRegionId);
          }
        }
      } catch (err) {
        console.error("[EventSuggestions] region context failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isSuperAdmin]);
  const { suggestions: rawSuggestions, loading } = useSuggestions(isSuperAdmin, vendorID);
  const { hosts, usersById } = useHosts(isSuperAdmin);

  const suggestions = [...rawSuggestions].sort((a, b) => {
    // Popularity sort: by the server-maintained likeCount, tie-broken by recency
    // so equally-liked (incl. zero-like) ideas keep a stable, sensible order.
    if (sortField === "likeCount") {
      const diff = (b.likeCount ?? 0) - (a.likeCount ?? 0);
      if (diff !== 0) return sortOrder === "desc" ? diff : -diff;
      return (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
    }
    const aTime = sortField === "suggestedDate"
      ? (a.suggestedDate?.seconds ?? 0)
      : (a.createdAt?.seconds ?? 0);
    const bTime = sortField === "suggestedDate"
      ? (b.suggestedDate?.seconds ?? 0)
      : (b.createdAt?.seconds ?? 0);
    return sortOrder === "desc" ? bTime - aTime : aTime - bTime;
  });

  // Suggestions predating regional support have no regionId; they're all SF, so
  // coalesce missing → sf-bay (matches the backend default) for both the filter
  // options and the match below.
  const suggestionRegion = (s) => s.regionId || "sf-bay";
  const regionNameById = regions.reduce((m, r) => { m[r.id] = r.name; return m; }, {});
  // Region buckets to offer: every region present in the data PLUS every
  // configured live/beta region — so a freshly-created city (e.g. an LA beta with
  // no suggestions yet) shows up and can be selected before its first generation.
  const regionFilterIds = [...new Set([
    ...rawSuggestions.map(suggestionRegion),
    ...regions.map((r) => r.id),
  ])].sort();

  const filteredSuggestions = suggestions.filter((s) => {
    if (regionFilter !== "all" && suggestionRegion(s) !== regionFilter) return false;
    if (pickupFilter === "all") return true;
    if (pickupFilter === "mine") return s.pickedUpBy === vendorID;
    return s.pickedUpBy === pickupFilter;
  });

  const myPicksCount = rawSuggestions.filter((s) => s.pickedUpBy === vendorID).length;
  const hostsWithPicks = hosts.filter((h) => rawSuggestions.some((s) => s.pickedUpBy === h.id));

  const handleGenerate = async () => {
    setGenerating(true);
    setPipelineSteps([]);

    const directives = { customNotes: customNotes.trim() };

    try {
      const response = await fetchFunction("eventSuggestionAgentStream", { directives, regionId: genRegionId });
      if (!response.ok) {
        // Rate-limit (429) and busy (409) rejections carry a user-facing message.
        let message = "";
        try {
          message = (await response.json())?.error || "";
        } catch { /* non-JSON body */ }
        throw new Error(message || `HTTP ${response.status}`);
      }

      await readSSEStream(response, (event) => {
        if (event.type === "step") {
          setPipelineSteps((prev) => {
            const nextStep = {
              step: typeof event.step === "number" ? event.step : prev.length,
              message: event.message || "Working...",
            };
            const existingIndex = prev.findIndex((item) => item.step === nextStep.step);
            if (existingIndex >= 0) {
              return prev.map((item, index) => index === existingIndex ? nextStep : item);
            }
            return [...prev, nextStep].sort((a, b) => a.step - b.step);
          });
        } else if (event.type === "done") {
          toast.success(`Generated ${event.data.count} new ideas`);
        }
        else if (event.type === "error") toast.error(event.message || "Generation failed");
      });
    } catch (err) {
      console.error("[EventSuggestions] Generate error:", err);
      const message = err.message && !/^HTTP \d+$/.test(err.message)
        ? err.message
        : "Failed to generate ideas. Please try again.";
      toast.error(message);
    } finally {
      setGenerating(false);
      setPipelineSteps([]);
    }
  };

  const updateStatus = async (suggestionId, newStatus) => {
    try {
      await EventSuggestionAPI.setStatus(suggestionId, newStatus);
    } catch (err) {
      console.error("[EventSuggestions] Status update error:", err);
      toast.error("Failed to update idea status");
    }
  };

  return (
    <>
      {/* Toolbar: Filter | View | Generate */}
      <div className="flex mt-4 items-center gap-2">
        {/* Filter icon — left */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            title="Filters"
            className={`relative inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-sm font-semibold ring-1 ring-inset shadow-sm transition-colors ${showFilters || pickupFilter !== "all" || regionFilter !== "all" || sortField !== "suggestedDate" || sortOrder !== "desc" ? "bg-primary text-white ring-primary" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
          >
            <FunnelIcon className="h-4 w-4" />
          </button>
          {/* Active filter dot */}
          {(pickupFilter !== "all" || regionFilter !== "all" || sortField !== "suggestedDate" || sortOrder !== "desc") && !showFilters && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary ring-2 ring-white dark:ring-gray-800" />
          )}
        </div>

        {/* View toggle — center */}
        <span className="isolate inline-flex rounded-md shadow-sm mx-auto">
          <button
            type="button"
            onClick={() => setView("cards")}
            title="Cards view"
            className={`relative inline-flex items-center gap-1.5 rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 ${view === "cards" ? "bg-primary text-white ring-primary" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden sm:inline">Cards</span>
          </button>
          <button
            type="button"
            onClick={() => setView("map")}
            title="Map view"
            className={`relative -ml-px inline-flex items-center gap-1.5 rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset focus:z-10 ${view === "map" ? "bg-primary text-white ring-primary" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
          >
            <MapIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Map</span>
          </button>
        </span>

        {/* Generate — right */}
        <div className="isolate inline-flex rounded-md shadow-sm">
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="relative inline-flex items-center gap-x-1.5 rounded-l-md bg-primary pl-3 pr-1 py-2 text-sm font-semibold text-white hover:bg-primaryDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {generating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <SparklesIcon className="h-4 w-4" aria-hidden="true" />
            )}
            {generating ? "Generating..." : "Generate"}
          </button>
          <button
            type="button"
            onClick={() => setShowDirectives((v) => !v)}
            title="Agent directives"
            className={`relative -ml-px inline-flex items-center rounded-r-md pl-1 pr-2.5 py-2 ring-1 ring-inset focus:z-10 transition-colors ${customNotes
              ? "bg-primaryDark text-white ring-primaryDark"
              : showDirectives
                ? "bg-primary text-white ring-primary"
                : "bg-primary text-white/70 ring-primary hover:text-white"
              }`}
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${showDirectives ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* How projections work — collapsible disclaimer */}
      <div className="mt-3 flex items-start gap-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
        <InformationCircleIcon className="h-4 w-4 mt-0.5 shrink-0 text-gray-400 dark:text-gray-500" />
        <p>
          <span className="font-semibold text-gray-600 dark:text-gray-300">Projected host earnings are estimates, not guarantees.</span>
          {showProjectionInfo && (
            <>
              {" "}They start from the <span className="font-medium">typical turnout of Gist events over the last few months</span> (~25 guests at ~$6 each), then adjust for audience fit, host track record, lead time, recent <span className="font-medium">platform momentum</span>, and a <span className="font-medium">location boost</span> from the venue's area (affluence, density &amp; proximity to tourist spots, via Google Places). A wealthy, dense, touristy area raises the projection; a quiet suburb lowers it. Actual payouts depend on real attendance and ticket sales.
            </>
          )}{" "}
          <button
            type="button"
            onClick={() => setShowProjectionInfo((v) => !v)}
            className="font-medium text-primary hover:text-primaryDark whitespace-nowrap"
          >
            {showProjectionInfo ? "See less" : "See more"}
          </button>
        </p>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-3 shadow-sm">
          <select
            value={`${sortField}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split("-");
              setSortField(field);
              setSortOrder(order);
            }}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-2.5 pr-7 py-1.5 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="suggestedDate-asc">Event Date ↑</option>
            <option value="suggestedDate-desc">Event Date ↓</option>
            <option value="createdAt-asc">Created ↑</option>
            <option value="createdAt-desc">Created ↓</option>
            <option value="likeCount-desc">Most Liked</option>
          </select>

          <button
            type="button"
            onClick={() => setPickupFilter(pickupFilter === "mine" ? "all" : "mine")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition-colors ${pickupFilter === "mine" ? "bg-primary text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"}`}
          >
            <HandRaisedIcon className="h-3.5 w-3.5" />
            My claims
            {myPicksCount > 0 && (
              <span className={`inline-flex items-center justify-center rounded-full h-4 min-w-[1rem] px-1 text-[10px] font-bold ${pickupFilter === "mine" ? "bg-white/30 text-white" : "bg-primary/10 text-primary"}`}>
                {myPicksCount}
              </span>
            )}
          </button>

          {isSuperAdmin && hostsWithPicks.length > 0 && (
            <select
              value={!["all", "mine"].includes(pickupFilter) ? pickupFilter : ""}
              onChange={(e) => setPickupFilter(e.target.value || "all")}
              className={`rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary ${!["all", "mine"].includes(pickupFilter) ? "border-primary bg-primary/5 text-primary font-semibold" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"}`}
            >
              <option value="">All hosts</option>
              {hostsWithPicks.map((h) => (
                <option key={h.id} value={h.id}>{h.name || h.email || h.id}</option>
              ))}
            </select>
          )}

          {regionFilterIds.length > 1 && (
            <select
              value={regionFilter}
              onChange={(e) => {
                const v = e.target.value;
                setRegionFilter(v);
                // The region you're viewing is the region you generate for:
                // a concrete filter sets the generate target; "all" falls back
                // to the SF default (still overridable in the directives panel).
                setGenRegionId(v === "all" ? "sf-bay" : v);
              }}
              className={`rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary ${regionFilter !== "all" ? "border-primary bg-primary/5 text-primary font-semibold" : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300"}`}
            >
              <option value="all">All regions</option>
              {regionFilterIds.map((id) => (
                <option key={id} value={id}>{regionNameById[id] || id}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Directives dropdown */}
      {showDirectives && (
        <div className="mt-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <div className="px-4 py-4 space-y-3">
            {isSuperAdmin && regions.length > 1 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Region</p>
                <select
                  value={genRegionId}
                  onChange={(e) => setGenRegionId(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}{r.status === "beta" ? " (beta)" : ""}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Which metro to generate ideas for.</p>
              </div>
            )}
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Agent Instructions</p>
            <textarea
              rows={3}
              value={customNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              placeholder="e.g. We need more events in the Peninsula / South Bay. Include one for hikers and outdoorsy people. Avoid anything over $30."
              className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder:text-gray-500 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {customNotes && (
              <button
                type="button"
                onClick={() => setCustomNotes("")}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 flex items-center gap-1"
              >
                <XMarkIcon className="h-3.5 w-3.5" /> Clear
              </button>
            )}
            {isSuperAdmin && (
              <div className="flex items-center gap-3 border-t border-gray-100 dark:border-gray-700/60 pt-3">
                <VideoToggle
                  enabled={videosEnabled}
                  disabled={savingVideos}
                  onToggle={handleToggleVideos}
                  label="Video generation"
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Video generation</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Makes an AI video for each new idea. Existing videos keep playing either way.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pipeline progress banner */}
      {generating && (
        <div className="mb-6 rounded-xl bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 px-5 py-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-3">
            AI agent is running — this can take up to 5 minutes
          </p>
          <div className="flex flex-col gap-2">
            {pipelineSteps.length === 0 && (
              <div className="flex items-center gap-2.5 text-sm text-blue-700 dark:text-blue-300">
                <div className="flex items-center justify-center h-6 w-6 rounded-full shrink-0 bg-blue-500">
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                </div>
                <span className="font-medium">Connecting to agent…</span>
              </div>
            )}
            {pipelineSteps.map((step, i) => {
              const isDone = i < pipelineSteps.length - 1;
              return (
                <div key={`${step.step}-${step.message}`} className="flex items-center gap-2.5 text-sm transition-opacity">
                  <div className={`flex items-center justify-center h-6 w-6 rounded-full shrink-0 ${isDone ? "bg-green-500" : "bg-blue-500"}`}>
                    {isDone ? (
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    ) : (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                    )}
                  </div>
                  <span className={isDone ? "text-green-700 dark:text-green-300 line-through" : "text-blue-700 dark:text-blue-300 font-medium"}>
                    {step.message}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Map View — always mounted to preserve map state; hidden via CSS when not active */}
      {!loading && (
        <div className={`mt-8 ${view !== "map" ? "hidden" : ""}`}>
          <MapView
            suggestions={filteredSuggestions}
            onUpdateStatus={updateStatus}
            isSuperAdmin={isSuperAdmin}
            vendorID={vendorID}
            hosts={hosts}
            usersById={usersById}
          />
        </div>
      )}

      {/* Cards Grid */}
      {view === "cards" && (
        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 items-start">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
          ) : filteredSuggestions.length === 0 ? (
            <EmptyState onGenerate={handleGenerate} generating={generating} />
          ) : (
            filteredSuggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onUpdateStatus={updateStatus}
                isSuperAdmin={isSuperAdmin}
                vendorID={vendorID}
                hosts={hosts}
                usersById={usersById}
                  />
            ))
          )}
        </div>
      )}
    </>
  );
}

const VideoToggle = ({ enabled, disabled, onToggle, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    aria-label={label}
    onClick={onToggle}
    aria-busy={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${enabled ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"
      } ${disabled ? "opacity-60" : ""}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${enabled ? "translate-x-5" : "translate-x-0"
        }`}
    />
  </button>
);

const SkeletonCard = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700 animate-pulse">
    <div className="w-full aspect-video bg-gray-200 dark:bg-gray-700 relative">
      <div className="absolute inset-0 bg-gradient-to-t from-gray-400/60 via-gray-400/10 to-transparent flex flex-col justify-end p-3 gap-1.5">
        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4" />
        <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2" />
      </div>
    </div>
  </div>
);

// Empty State
const EmptyState = ({ onGenerate, generating }) => {
  return (
    <div className="col-span-full mt-8 text-center py-12">
      <SparklesIcon className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
      <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No ideas yet</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Click Generate to create new AI-powered event ideas.</p>
      <div className="mt-6">
        <button
          type="button"
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-x-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <SparklesIcon className="-ml-0.5 h-5 w-5" />
          {generating ? "Generating..." : "Generate Ideas"}
        </button>
      </div>
    </div>
  );
};

// Custom hook to fetch all hosts — only used by superAdmin
// IDs come from vendors collection; display info comes from users collection
const useHosts = (isSuperAdmin) => {
  const [hosts, setHosts] = useState([]);
  const [usersById, setUsersById] = useState({});

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const vendors = await VendorAPI.listVendorDocs();
        const vendorIds = vendors.map((v) => v.id);

        // Only the hosts' own user docs. This used to download the ENTIRE users
        // collection — every member's phone, email, device token and Stripe id —
        // to put display names on a host dropdown.
        const users = await fetchUsersByDocIds(db, vendorIds);
        if (cancelled) return;

        const usersMap = new Map(users.map((u) => [u.id, u]));
        const hosts = vendors.map((v) => {
          const userRecord = usersMap.get(v.id);
          return userRecord ? userRecord : { id: v.id, ...v.data };
        });
        setHosts(hosts);
        setUsersById(Object.fromEntries(usersMap));
      } catch (err) {
        console.error("[EventSuggestions] Hosts fetch error:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  return { hosts, usersById };
};

// Custom hook for real-time suggestions
const useSuggestions = (isSuperAdmin, vendorID) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = EventSuggestionAPI.subscribeAll(
      (data) => {
        // SuperAdmins see everything; regular hosts only see unpicked or their own
        const filtered = isSuperAdmin
          ? data
          : data.filter((s) => !s.pickedUpBy || s.pickedUpBy === vendorID);
        setSuggestions(filtered);
        setLoading(false);
      },
      (err) => {
        console.error("[EventSuggestions] Snapshot error:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [isSuperAdmin, vendorID]);

  return { suggestions, loading };
};

export { SuggestionCard };
